/**
 * @fileoverview Template Card component for visual display
 */
import { useState } from 'react';
import { FileText, MoreVertical, Edit, Copy, Trash2, Download, Calendar, HardDrive } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDateFormat } from '@/hooks/useDateFormat';

export const TemplateCard = ({ template, onEdit, onClone, onDelete }) => {
    const { t } = useLanguage();
    const [showMenu, setShowMenu] = useState(false);
    const { formatDate } = useDateFormat();



    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const getCategoryBadge = (category) => {
        const badges = {
            training: { label: t('templates.category_training', 'Capacitación'), color: 'bg-blue-100 text-blue-700' },
            mro: { label: t('templates.category_mro', 'Mantenimiento'), color: 'bg-orange-100 text-orange-700' },
            custom: { label: t('templates.category_custom', 'Personalizado'), color: 'bg-purple-100 text-purple-700' }
        };
        return badges[category] || badges.custom;
    };

    const badge = getCategoryBadge(template.category);
    const fieldsCount = Array.isArray(template.fields) ? template.fields.length : 0;

    // Normalize path to use forward slashes for web URLs
    const imagePath = template.file_path ? `/uploads/${template.file_path.replace(/\\/g, '/')}` : null;
    console.log('Template image path:', imagePath);

    return (
        <div className="bg-white rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all duration-300 overflow-hidden group">
            {/* SVG Preview */}
            <div className="aspect-[1.4/1] bg-slate-50 border-b border-slate-200 relative overflow-hidden flex items-center justify-center p-4">
                {imagePath ? (
                    <img
                        src={imagePath}
                        alt={template.name}
                        className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                            console.error('Failed to load image:', imagePath);
                            e.target.style.display = 'none';
                        }}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center text-slate-400">
                        <FileText className="w-16 h-16 mb-2" />
                        <p className="text-sm">{t('templates.no_preview', 'Sin vista previa')}</p>
                    </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
            </div>

            {/* Card Content */}
            <div className="p-4">
                {/* Header with title and menu */}
                <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-slate-800 text-lg leading-tight flex-1 pr-2">
                        {template.name}
                    </h3>
                    <div className="relative">
                        <button
                            onClick={() => setShowMenu(!showMenu)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                            <MoreVertical className="w-5 h-5 text-slate-600" />
                        </button>

                        {/* Dropdown Menu */}
                        {showMenu && (
                            <>
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setShowMenu(false)}
                                />
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[160px] z-20">
                                    <button
                                        onClick={() => { onEdit?.(template); setShowMenu(false); }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                    >
                                        <Edit className="w-4 h-4" />
                                        {t('common.edit', 'Editar')}
                                    </button>
                                    <button
                                        onClick={() => { onClone?.(template); setShowMenu(false); }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                    >
                                        <Copy className="w-4 h-4" />
                                        {t('templates.clone', 'Clonar')}
                                    </button>
                                    <a
                                        href={`/uploads/${template.file_path}`}
                                        download
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                        onClick={() => setShowMenu(false)}
                                    >
                                        <Download className="w-4 h-4" />
                                        {t('templates.download', 'Descargar')}
                                    </a>
                                    <hr className="my-1 border-slate-200" />
                                    <button
                                        onClick={() => { onDelete?.(template); setShowMenu(false); }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        {t('common.delete', 'Eliminar')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Category and File Size */}
                <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${badge.color}`}>
                        {badge.label}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        {formatFileSize(template.file_size)}
                    </span>
                </div>

                {/* Upload Date */}
                <div className="flex items-center gap-1 text-xs text-slate-500 mb-3">
                    <Calendar className="w-3 h-3" />
                    <span>{t('templates.uploaded', 'Subido')}: {formatDate(template.created_at)}</span>
                </div>

                {/* Description */}
                {template.description && (
                    <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                        {template.description}
                    </p>
                )}

                {/* Fields Count */}
                {fieldsCount > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-500">
                            <span className="font-semibold text-blue-600">{fieldsCount}</span> {t('templates.dynamic_fields', 'campos dinámicos')}
                        </p>
                    </div>
                )}
            </div>

            {/* Action Buttons (visible on hover) */}
            <div className="px-4 pb-4 pt-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex gap-2">
                    <button
                        onClick={() => onEdit?.(template)}
                        className="flex-1 py-2 px-3 rounded-lg bg-blue-50 text-blue-600 text-sm font-medium hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                    >
                        <Edit className="w-3.5 h-3.5" />
                        {t('common.edit', 'Editar')}
                    </button>
                    <button
                        onClick={() => onClone?.(template)}
                        className="flex-1 py-2 px-3 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-1"
                    >
                        <Copy className="w-3.5 h-3.5" />
                        {t('templates.clone', 'Clonar')}
                    </button>
                </div>
            </div>
        </div>
    );
};
